import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import { getPropagationLogs } from '../../../../Redux/recordPropagation/action';
import { downloadFullS3File } from '../../../../Redux/sftpClient/action';
import { handleDownloadFileUrl } from '../../Dashboard/functions';
import queueDatatableCustomStyles from '../../../../Layout/Elements/QueueDatatableCustomStyles';

const getLogRows = (raw) => {
    if (Array.isArray(raw?.results?.data)) return raw.results.data;
    if (Array.isArray(raw?.results)) return raw.results;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
};

const HIDDEN_KEYS = [
    'id',
    'pk',
    'record_propagation_id',
    'client_id',
    'client_name',
    'facility_id',
    'facility_name',
    'source_job_id',
    'target_job_id',
    'source_record_id',
    'target_record_id',
    'log_file_path',
    'table_name',
    'record_count',
];

const NARROW_WIDTHS = {
    source_job: '100px',
    target_job: '100px',
    status: '100px',
    created_date: '160px',
};

const KEY_ORDER = ['source_job', 'target_job', 'status', 'message', 'created_date'];

const sortKeysByOrder = (keys) => {
    const ordered = KEY_ORDER.filter((key) => keys.includes(key));
    const rest = keys.filter((key) => !KEY_ORDER.includes(key));
    return [...ordered, ...rest];
};

const formatLabel = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (key, value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (/date|time/i.test(key)) {
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('MM/DD/YYYY HH:mm:ss') : value;
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
};

const PropagationLogsModal = ({ show, onClose, recordId }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloadingKey, setDownloadingKey] = useState(null);

    const fetchLogs = () => {
        if (!recordId) return;
        setLoading(true);
        setError('');
        getPropagationLogs({ id: recordId })((response) => {
            setLoading(false);
            if (response?.status) {
                setLogs(getLogRows(response.data));
            } else {
                setLogs([]);
                setError(response?.message || 'Failed to load propagation logs.');
            }
        });
    };

    useEffect(() => {
        if (show && recordId) {
            fetchLogs();
        }
        if (!show) {
            setLogs([]);
            setError('');
        }
    }, [show, recordId]);

    const handleDownloadLogFile = (row) => {
        const filePath = row?.log_file_path;
        const key = row?.id ?? row?.pk ?? filePath;
        if (!filePath || downloadingKey) return;
        setDownloadingKey(key);
        downloadFullS3File({ file_path: filePath })((response) => {
            setDownloadingKey(null);
            if (response?.status && response?.data?.signed_url) {
                handleDownloadFileUrl(response.data.signed_url);
            } else {
                setError(response?.message || 'Failed to download log file.');
            }
        });
    };

    const columns = useMemo(() => {
        const keys = sortKeysByOrder(Object.keys(logs?.[0] || {}).filter((key) => !HIDDEN_KEYS.includes(key)));
        return [
            {
                id: 's_no',
                name: 'S NO',
                selector: (row, index) => index + 1,
                width: '70px',
                grow: 0,
            },
            ...keys.map((key) => {
                const isNarrow = Object.prototype.hasOwnProperty.call(NARROW_WIDTHS, key);
                const isMessage = key === 'message';
                return {
                    id: key,
                    name: formatLabel(key),
                    selector: (row) => formatValue(key, row?.[key]),
                    cell: (row) => (
                        <Tooltip disableInteractive title={formatValue(key, row?.[key])} placement="top" arrow>
                            <span className="text-truncate" style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {formatValue(key, row?.[key])}
                            </span>
                        </Tooltip>
                    ),
                    sortable: true,
                    wrap: true,
                    grow: isNarrow ? 0 : (isMessage ? 3 : 2),
                    width: isNarrow ? NARROW_WIDTHS[key] : undefined,
                    minWidth: isNarrow ? undefined : (isMessage ? '300px' : '200px'),
                };
            }),
            {
                id: 'download_log',
                name: 'Log',
                center: true,
                width: '70px',
                grow: 0,
                cell: (row) => {
                    if (!row?.log_file_path) return null;
                    const key = row?.id ?? row?.pk ?? row?.log_file_path;
                    const isDownloading = downloadingKey === key;
                    return (
                        <Tooltip disableInteractive title="Download Log" placement="top" arrow>
                            <button
                                type="button"
                                className="btn btn-sm border-0 p-0"
                                style={{ color: '#6ba2b6' }}
                                disabled={isDownloading}
                                onClick={() => handleDownloadLogFile(row)}
                            >
                                {isDownloading
                                    ? <Spinner animation="border" size="sm" />
                                    : <i className="bi bi-download fs-6"></i>}
                            </button>
                        </Tooltip>
                    );
                },
            },
        ];
    }, [logs, downloadingKey]);

    return (
        <Modal show={show} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center justify-content-between">
                    <span className="mx-3">Propagation Logs</span>
                    <Tooltip title="Refresh" placement="top" arrow>
                        <Button
                            className="btn btn-light btn-active-primary"
                            onClick={fetchLogs}
                            disabled={loading}
                        >
                            {loading
                                ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                : <i className="bi-arrow-clockwise"></i>}
                        </Button>
                    </Tooltip>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: '40vh' }}>
                {error && !loading && (
                    <div className="alert alert-danger" role="alert">{error}</div>
                )}
                <DataTable
                    className="custom-datatable w-100 p-0"
                    fixedHeader
                    columns={columns}
                    data={logs}
                    highlightOnHover
                    responsive
                    customStyles={queueDatatableCustomStyles}
                    progressPending={loading}
                    progressComponent={<Spinner animation="border" />}
                    noDataComponent={<div className="py-4 text-muted">No logs found.</div>}
                />
            </Modal.Body>
        </Modal>
    );
};

export default PropagationLogsModal;
